import { Button, Link, Linkify } from '@mm/components';
import { ArrowStatefulIcon, InfoIcon, PlayIcon } from '@mm/icons';
import { useAttributeCRUDActions } from '@mm/state/actions/AttributeCRUDActions';
import { useInstagramCRUDActions } from '@mm/state/actions/InstagramCRUDActions';
import { identifyChannel } from '@mm/state/helpers/live-chat-helpers';
import { ContactVariable } from '@mm/state/interfaces/contacts';
import {
  ExtendedContactMessage,
  isContactMessage,
} from '@mm/state/model/ContactMessageModel';
import {
  IgMessageStructure,
  LivechatMessageModel,
  MessageKind,
  MessageStructure,
} from '@mm/state/model/LivechatMessageModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { ReactComponent as AttachmentDownloadIcon } from 'assets/attachment-download.svg';
import classNames from 'classnames';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Popover from 'react-tiny-popover';
import validator from 'validator';
import {
  EMAIL_MATCH_REGEX,
  PHONE_MATCH_REGEX,
} from '../../../../../../common/constants/validations';
import { successToast } from '../../../../../../common/helpers/toast';
import { ProfileImage } from '../../ProfileImage/ProfileImage';
import { DeletedMessage } from '../DeletedMessage/DeletedMessage';
import { IGCommentGuardMessage } from '../IGCommentGuardMessage/IGCommentGuardMessage';
import { IGLiveCommentMessage } from '../IGLiveCommentMessage/IGLiveCommentMessage';
import { IGStoryMessage } from '../IGStoryMessage/IGStoryMessage';
import { ImageMessage } from '../ImageMessage/ImageMessage';
import { messageStructureParser } from '../messageStructureParser';
import {
  SearchResultText,
  SearchResultTextProps,
} from '../SearchResultText/SearchResultText';
import { StartedFrom } from '../StartedFrom/StartedFrom';
import { ReactComponent as ClickIcon } from './click.svg';
import { ReactComponent as EyeIcon } from './eye.svg';
import './IncomingMessage.pcss';

const PHONE = 'PHONE';
const EMAIL = 'EMAIL';

type Props = {
  messages?: LivechatMessageModel[];
  message: LivechatMessageModel;
  nextMessage?: LivechatMessageModel | ExtendedContactMessage;
  messageKey: string;
  hoverMessageKey: string | undefined;
  setHoverMessageKey: (key: string | undefined) => void;
  profilePic: string | undefined;
  fullName: string;
  variables: ContactVariable[];
  refreshSelectedContact: () => void;
  isFirstMessage: boolean;
  contactId?: number;
  isSearch?: boolean;
} & Omit<SearchResultTextProps, 'messageText'>;

type MediaProps = {
  url: string;
};

const VideoAttachement = ({ url }: MediaProps) => (
  <div className="incoming-message__body-video-container">
    <video
      controls
      muted
      autoPlay
      loop
      className="incoming-message__body-video"
    >
      <source src={url} />
    </video>
  </div>
);

const AudioAttachement = ({ url }: MediaProps) => (
  <div className="incoming-message__body-video-container">
    <audio controls className="incoming-message__body-video">
      <source src={url} />
    </audio>
  </div>
);

// tslint:disable-next-line: cyclomatic-complexity
export const IncomingMessage = ({
  messages,
  message,
  nextMessage,
  messageKey,
  isFirstMessage,
  hoverMessageKey,
  setHoverMessageKey,
  profilePic,
  isSearch,
  maybeGetRef,
  getMatchHighlightClassName,
  searchString,
  fullName,
  variables,
  contactId,
  refreshSelectedContact,
}: Props) => {
  const {
    getDisplayText,
    getTextIsEmojiOnly,
    getAttachmentLinksWithTypes,
    getFallbackAttachment,
    isOTNConsent,
    isGetStarted,
    isMessengerAd,
    isSponsoredMessage,
    isChatWidget,
    isShortlink,
    isIGShortLink,
    isCheckboxOptin,
    isSendToMessengerOptin,
    isLandingPage,
    isCommentGuard,
    isIceBreaker,
    isUserChatMessage,
    isOptinPopup,
    isSMSOptin,
    getWebsiteChatUrl,
    getDisplayImageSrc,
    isDeleted,
    isIGStory,
    isIgInlineReply,
    getDisplayVideoSrc,
    getDisplayAudioSrc,
    isMobileMonkeyAppEmbeddedChat,
    isMobileMonkeyAppEmbeddedChatLLR,
    isIGCommentGuard,
    isIGWelcomer,
    isStoryMention,
    isQnA,
    isIGCatchAllEngagements,
    isFBCatchAllEngagements,
    getIsIGCommentGuardMessage,
    getIsIGLiveCommentMessage,
    getHiddenBodyText,
    getEmailAttachmentsNames,
  } = messageStructureParser(message);

  const channel = identifyChannel(message.remoteUserId);
  const hiddenBodyText = getHiddenBodyText();
  const [showHiddenBody, setShowHiddenBody] = useState(false);
  const displayText = getDisplayText();
  const attachmentLinksWithTypes = getAttachmentLinksWithTypes();
  const fallbackAttachment = getFallbackAttachment();
  const otnConsent = isOTNConsent();
  const { addAttribute } = useAttributeCRUDActions();
  const { openGeneralConfirmModal } = useModalActions();
  const imageSrc = getDisplayImageSrc();
  const videoSrc = getDisplayVideoSrc();
  const audioSrc = getDisplayAudioSrc();
  const isDeletedMessage = isDeleted();
  const isIGStoryMessage = isIGStory();
  const isIGCommentGuardMessage = getIsIGCommentGuardMessage();
  const isIgInlineReplyMessage = isIgInlineReply();
  const isIGLiveCommentMessage = getIsIGLiveCommentMessage();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isEmojiOnly = useMemo(() => getTextIsEmojiOnly(), [displayText]);

  const initiatedFromGetStarted = isGetStarted();
  const initiatedFromQnA = isQnA();
  const initiatedFromIceBreaker = isIceBreaker(isFirstMessage);
  const initiatedFromCustomerChat = isChatWidget(isFirstMessage);
  const initiatedFromCustomerChatByUser = isUserChatMessage(isFirstMessage);
  const initiatedFromShortlink = isShortlink(isFirstMessage);
  const initiatedFromIGShortlink = isIGShortLink(isFirstMessage);
  const initiatedFromLanding = isLandingPage(isFirstMessage);
  const initiatedFromCheckbox = isCheckboxOptin(isFirstMessage);
  const initiatedFromSTM = isSendToMessengerOptin(isFirstMessage);
  const initiatedFromSponsoredMessage = isSponsoredMessage();
  const initiatedFromMessengerAd = isMessengerAd();
  const initiatedFromCG = isCommentGuard();
  const initiatedFromOptinPopup = isOptinPopup(isFirstMessage);
  const initiatedFromSMSOptin = isSMSOptin(isFirstMessage);
  const initiatedFromIGCG = isIGCommentGuard(isFirstMessage);
  const initiatedFromIGWelcomer = isIGWelcomer(isFirstMessage);
  const initiatedFromIGCatchAll = isIGCatchAllEngagements(isFirstMessage);
  const initiatedFromFBCatchAll = isFBCatchAllEngagements(isFirstMessage);
  const initiatedFromStoryMention = isStoryMention(isFirstMessage);
  const initiatedFromMobileMonkeyAppEmbeddedChat =
    isMobileMonkeyAppEmbeddedChat(isFirstMessage);
  const initiatedFromMobileMonkeyAppEmbeddedChatLLR =
    isMobileMonkeyAppEmbeddedChatLLR(isFirstMessage);
  const [showEmbeddedChatDetails, setShowEmbeddedChatDetails] = useState(false);
  const [mobilePhone, setMobilePhone] = useState('');
  const [email, setEmail] = useState('');
  const [inLineReplyContent, setInLineReplyContent] = useState<
    string | JSX.Element
  >('...loading');

  const { getLocalIGMessage, getRemoteIGMessage } = useInstagramCRUDActions();
  const fbPageId = useSelector(activeFacebookPageIdSelector);

  useEffect(() => {
    if (!isIgInlineReplyMessage) return;

    const getIgReplyContent = (
      parsableStructure: IgMessageStructure | MessageStructure,
    ) => {
      const {
        getDisplayText,
        getAttachmentLinksWithTypes,
        getDisplayImageSrc,
        getDisplayVideoSrc,
        getDisplayAudioSrc,
      } = messageStructureParser({ structure: parsableStructure });

      const displayImageSrc = getDisplayImageSrc();
      const displayVideoSrc = getDisplayVideoSrc();
      const displayAudioSrc = getDisplayAudioSrc();
      const displayText = getDisplayText();
      const attachmentLinksWithTypes = getAttachmentLinksWithTypes();

      if (displayImageSrc) return <ImageMessage url={displayImageSrc} />;
      if (displayVideoSrc) return <VideoAttachement url={displayVideoSrc} />;
      if (displayAudioSrc) {
        return (
          <div className="incoming-message-audio__container">
            <div className="incoming-message-audio__title">Voice Message:</div>
            <AudioAttachement url={displayAudioSrc} />
          </div>
        );
      }

      if (attachmentLinksWithTypes) {
        const a = attachmentLinksWithTypes[0];

        if (a) {
          if (a.type === 'image' && a.url) {
            return <ImageMessage url={a.url} />;
          }
          if (a.type === 'video' && a.url) {
            return <VideoAttachement url={a.url} />;
          }
          if (a.type === 'audio' && a.url) {
            return (
              <div className="incoming-message-audio__container">
                <div className="incoming-message-audio__title">
                  Voice Message:
                </div>
                <AudioAttachement url={a.url} />
              </div>
            );
          }
        }
      }

      if (displayText) return displayText;
    };

    const getIgMessage = async () => {
      if (message.structure?.replyTo?.mid && message.remoteUserId) {
        const messageResponse = await getLocalIGMessage(
          message.structure.replyTo?.mid,
          fbPageId,
          message.remoteUserId,
        );

        if (messageResponse?.structures?.length) {
          let counter = 0;

          let content: ReturnType<typeof getIgReplyContent>;
          while (counter < messageResponse.structures.length) {
            content = getIgReplyContent(messageResponse.structures[counter]);
            if (content) {
              break;
            }
            counter = counter + 1;
          }
          if (content) {
            setInLineReplyContent(content);
            return;
          }
        }

        const remoteResponse = await getRemoteIGMessage(
          message.structure.replyTo?.mid,
          fbPageId,
        );

        setInLineReplyContent(remoteResponse?.message || '');
      }
    };

    const preLoadedMessage = messages?.find(
      m => m.structure?.mid === message.structure.replyTo?.mid,
    );

    if (preLoadedMessage) {
      const content = getIgReplyContent(preLoadedMessage?.structure);

      if (content) {
        setInLineReplyContent(content);
        return;
      }
    }

    getIgMessage();
  }, []);

  const smsKeyword = useMemo(() => {
    // SMS Keywords are a bit tricky because we detect them after we've written
    // the initial message into cassandra, so the messaging record with the source data
    // ends up after the message.
    if (
      !isFirstMessage ||
      !nextMessage ||
      isContactMessage(nextMessage) ||
      !message.remoteUserId.startsWith('+')
    ) {
      return false;
    }
    const { getSMSKeyword } = messageStructureParser(nextMessage);
    return getSMSKeyword();
  }, [isFirstMessage, message, nextMessage]);

  useEffect(() => {
    const matchEmails = displayText.match(EMAIL_MATCH_REGEX);
    const matchPhones = displayText.match(PHONE_MATCH_REGEX);

    if (matchEmails?.length) {
      const isEmail = validator.isEmail(matchEmails[0].trim());
      if (isEmail) {
        setEmail(matchEmails[0]);
      }
    }
    if (matchPhones?.length) {
      const isMobilePhone = validator.isMobilePhone(
        matchPhones[0].trim(),
        'any',
      );
      if (isMobilePhone) {
        setMobilePhone(matchPhones[0]);
      }
    }
  }, []);

  const websiteChatUrl = getWebsiteChatUrl(isFirstMessage);

  const showStartedFrom = [
    initiatedFromMessengerAd,
    initiatedFromCustomerChat,
    initiatedFromShortlink,
    initiatedFromIGShortlink,
    initiatedFromCG,
    initiatedFromSTM,
    initiatedFromLanding,
    initiatedFromOptinPopup,
    initiatedFromSMSOptin,
    smsKeyword,
    initiatedFromMobileMonkeyAppEmbeddedChat,
    initiatedFromMobileMonkeyAppEmbeddedChatLLR,
    initiatedFromIGCG,
    initiatedFromStoryMention,
    initiatedFromIGWelcomer,
    initiatedFromFBCatchAll,
    initiatedFromIGCatchAll,
  ].some(Boolean);

  const showMessageContainer = [
    displayText,
    otnConsent,
    attachmentLinksWithTypes,
    fallbackAttachment,
    imageSrc,
    isIGStoryMessage,
    videoSrc,
    audioSrc,
  ].some(Boolean);

  const isContactReadEmailEvent =
    channel === 'email' && message.kind === MessageKind.read;
  const isContactLinkClickedEmailEvent =
    channel === 'email' && message.kind === MessageKind.linkClicked;
  const isContactEvent =
    isContactReadEmailEvent || isContactLinkClickedEmailEvent;

  const renderNone = useMemo(
    () =>
      !showStartedFrom &&
      !initiatedFromCheckbox &&
      !showMessageContainer &&
      !isContactEvent,
    [
      showStartedFrom,
      initiatedFromCheckbox,
      showMessageContainer,
      isContactEvent,
    ],
  );

  if (renderNone) {
    return null;
  }

  const fallbackAttachmentUrl =
    fallbackAttachment?.url || fallbackAttachment?.payload?.url;
  const fallbackAttachmentTitle =
    fallbackAttachment?.title || fallbackAttachment?.payload?.title;
  const openFallbackUrl = () =>
    fallbackAttachmentUrl && window.open(fallbackAttachmentUrl, '_blank');

  const showSuccessfulConfirmationToast = (type: 'EMAIL' | 'PHONE' | 'all') => {
    const text =
      type === 'all'
        ? 'Email and phone number'
        : type === EMAIL
        ? 'Email'
        : 'Phone number';
    successToast(`${text} successfully saved to this contact`);
  };

  const renderMessageStarters = () => {
    return (
      <>
        {initiatedFromSponsoredMessage && (
          <StartedFrom
            payload={
              'Sponsored Message ' +
              (message.structure.smRemoteAdId
                ? `(Ad id: ${message.structure.smRemoteAdId})`
                : '')
            }
          />
        )}
        {initiatedFromCheckbox && (
          <StartedFrom
            payload={
              'Facebook Opt-in (Checkbox Plugin' +
              (message.structure.promoterName
                ? ` - ${message.structure.promoterName})`
                : ')')
            }
          />
        )}
        {initiatedFromGetStarted && <StartedFrom payload={'Facebook Page'} />}
        {initiatedFromIceBreaker && (
          <StartedFrom
            payload={
              message.remoteUserId.startsWith('ig')
                ? 'Instagram Icebreaker'
                : 'Icebreaker'
            }
          />
        )}
      </>
    );
  };

  const igInlineReply = inLineReplyContent && (
    <div className="incoming-message__inline-reply--container">
      <div className="incoming-message__inline-reply--who">Replied to you</div>
      <div className="incoming-message__inline-reply--text-container">
        <div className="incoming-message__inline-reply">
          {inLineReplyContent}
        </div>
      </div>
    </div>
  );

  const renderConversationStartedFrom = () => {
    const renderers: [boolean, () => string | React.ReactNode][] = [
      [
        initiatedFromSTM,
        () =>
          'Facebook Opt-in (Send-to-Messenger Button' +
          (message.structure.promoterName
            ? ` - ${message.structure.promoterName})`
            : ')'),
      ],
      [
        initiatedFromLanding,
        () =>
          'Landing Page ' +
          (message.structure.promoterName
            ? `(${message.structure.promoterName})`
            : ''),
      ],
      [
        initiatedFromMessengerAd,
        () =>
          'Messenger Ad ' +
          (message.structure.adId ? `(Ad id: ${message.structure.adId})` : ''),
      ],
      [
        initiatedFromIGWelcomer,
        () => `Instagram DM Responder(${message.structure.promoterName})`,
      ],
      [initiatedFromIGCatchAll, () => `Instagram Engagement Autoresponder`],
      [initiatedFromFBCatchAll, () => `Facebook Engagement Autoresponder`],
      [
        initiatedFromStoryMention,
        () => `Instagram Story Mention (${message.structure.promoterName})`,
      ],
      [
        initiatedFromIGCG,
        () => `Instagram Comment Guard (${message.structure.promoterName})`,
      ],
      [
        initiatedFromCG,
        () => `Comment Guard (${message.structure.fbGuardName})`,
      ],
      [
        initiatedFromCustomerChat,
        () => (
          <>
            Website Chat
            {websiteChatUrl && (
              <>
                {' '}
                (
                <Link
                  className="incoming-message__initiated-from-url"
                  href={websiteChatUrl.href}
                  isBlank
                >
                  {websiteChatUrl.hostname}
                  {websiteChatUrl.pathname !== '/'
                    ? websiteChatUrl.pathname
                    : ''}
                </Link>
                )
              </>
            )}
          </>
        ),
      ],
      [
        initiatedFromShortlink,
        () =>
          message.structure.promoterName?.startsWith('Opt-In Widget')
            ? 'Opt-in Widget'
            : 'Share Link',
      ],
      [initiatedFromIGShortlink, () => 'Instagram Share Link'],
      [
        initiatedFromOptinPopup,
        () => (
          <>
            Opt-in Popup{' '}
            {message.structure.promoterName &&
              `(${message.structure.promoterName})`}
          </>
        ),
      ],
      [initiatedFromSMSOptin, () => 'Opt-in Widget'],
      [Boolean(smsKeyword), () => `SMS Keyword (${smsKeyword})`],
      [
        initiatedFromMobileMonkeyAppEmbeddedChat,
        () => {
          const {
            facebookPageName,
            facebookPageAdminLink,
            currentPlan,
            subscriptionAdminLink,
            companyName,
            companyAdminLink,
            leadType,
          } = message.structure.referral || {};
          return (
            <>
              Customers.ai App Embedded Chat{' '}
              <ArrowStatefulIcon
                direction={showEmbeddedChatDetails ? 'up' : 'down'}
                onClick={() => setShowEmbeddedChatDetails(s => !s)}
              />
              {showEmbeddedChatDetails && (
                <div>
                  {facebookPageName && (
                    <div>
                      <strong>Facebook Page:</strong> {facebookPageName}{' '}
                      {facebookPageAdminLink && (
                        <Link href={facebookPageAdminLink} isBlank>
                          <InfoIcon />
                        </Link>
                      )}
                    </div>
                  )}
                  {companyName && (
                    <div>
                      <strong>Company:</strong> {companyName}{' '}
                      {companyAdminLink && (
                        <Link href={companyAdminLink} isBlank>
                          <InfoIcon />
                        </Link>
                      )}
                    </div>
                  )}
                  {currentPlan && (
                    <div>
                      <strong>Plan:</strong> {currentPlan}{' '}
                      {subscriptionAdminLink && (
                        <Link href={subscriptionAdminLink} isBlank>
                          <InfoIcon />
                        </Link>
                      )}
                    </div>
                  )}
                  {leadType && (
                    <div>
                      <strong>Lead Type:</strong> {leadType}
                    </div>
                  )}
                </div>
              )}
            </>
          );
        },
      ],
      [
        initiatedFromMobileMonkeyAppEmbeddedChatLLR,
        () => {
          const { llrDomain } = message.structure.referral || {};
          return (
            <>
              Customers.ai App Embedded Chat from LLR for{' '}
              {llrDomain || '[unknown domain]'}
            </>
          );
        },
      ],
    ];
    const renderer = renderers.find(([match]) => match);
    if (renderer) {
      return <StartedFrom payload={renderer[1]()} />;
    }
    return null;
  };

  const showSuccessfulConfirmationModal = async (
    type: 'EMAIL' | 'PHONE' | 'all',
  ) => {
    if (contactId) {
      if (type === 'all') {
        await addAttribute(contactId, PHONE, mobilePhone);
        await addAttribute(contactId, EMAIL, email);
        showSuccessfulConfirmationToast('all');
      } else {
        const attributeValue = (PHONE && mobilePhone) || (EMAIL && email);
        await addAttribute(contactId, type, attributeValue);
        showSuccessfulConfirmationToast(type);
      }
      await refreshSelectedContact();
    }
  };

  const showConfirmationModal = (type: 'EMAIL' | 'PHONE' | 'all') => {
    if (contactId) {
      const displayValue =
        type === 'all'
          ? `${mobilePhone}, ${email}`
          : (PHONE && mobilePhone) || (EMAIL && email);

      openGeneralConfirmModal({
        headerText: (
          <span className="incoming-message__confirmation-modal-header">
            Are you sure you want to save{' '}
            <span className="incoming-message__confirmation-modal-attribute-value">
              {displayValue}
            </span>{' '}
            to this contact?
          </span>
        ),
        messageText: (
          <div className="incoming-message__confirmation-modal-contact">
            <ProfileImage altText={''} profilePictureUrl={profilePic} big />
            {fullName}
          </div>
        ),
        okButtonText: 'Save',
        cancelButtonFirst: true,
        okButtonStyling: {
          blue: true,
        },
        cancelButtonColor: 'transparent',
        modalClassname: 'incoming--message--confirmation-modal',
        onCloseHandler: async (ok: boolean) => {
          if (ok) {
            showSuccessfulConfirmationModal(type);
          }
        },
      });
    }
  };

  const saveToContact = async () => {
    if (mobilePhone && email) {
      const attributes = variables.filter(variable => {
        return (
          variable.variable.name === PHONE || variable.variable.name === EMAIL
        );
      });
      if (attributes.length === 2) {
        showConfirmationModal('all');
      } else {
        showSuccessfulConfirmationModal('all');
      }
    } else {
      const variableName = (mobilePhone && PHONE) || (email && EMAIL);
      if (variableName) {
        const attribute = variables.find(variable => {
          return variable.variable.name === variableName;
        });
        if (attribute) {
          showConfirmationModal(variableName);
        } else {
          showSuccessfulConfirmationModal(variableName);
        }
      }
    }
  };
  const showMessageActions = !!(
    message.structure.emoji ||
    mobilePhone ||
    email
  );

  const profileImageClassname = classNames({
    'incoming--message--profile-image__moved': isIGCommentGuardMessage,
  });

  if (isContactEvent) {
    if (isContactLinkClickedEmailEvent) {
      return (
        <div className="incoming-message__email-event">
          <ClickIcon />
          <strong>Contact clicked a Link in the Email</strong>
          <div>
            <a
              href={message.structure.target}
              target="_blank"
              rel="noreferrer noopener"
            >
              {message.structure.link}
            </a>
          </div>
        </div>
      );
    }
    return (
      <div className="incoming-message__email-event">
        <EyeIcon />
        <strong>Contact opened</strong>
        <div>
          {message.structure.subject ? (
            message.structure.subject
          ) : (
            <i>Subject Unknown</i>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      {showStartedFrom ? (
        renderConversationStartedFrom()
      ) : (
        <>
          {initiatedFromCustomerChatByUser && (
            <StartedFrom payload="Website Chat" />
          )}
        </>
      )}
      {renderMessageStarters()}
      {isIgInlineReplyMessage && igInlineReply}
      {showMessageContainer && (
        <>
          <div className="incoming--message--container">
            <ProfileImage
              altText={''}
              profilePictureUrl={profilePic}
              big
              className={profileImageClassname}
            />
            <div
              key={messageKey}
              onMouseEnter={() => {
                if (hoverMessageKey !== messageKey) {
                  setHoverMessageKey(messageKey);
                }
              }}
              onMouseLeave={() => setHoverMessageKey(undefined)}
              className="incoming--message--container-item"
            >
              {isDeletedMessage ? (
                <DeletedMessage />
              ) : (
                <>
                  {(displayText || otnConsent) &&
                    !isIGCommentGuardMessage &&
                    !isIGLiveCommentMessage && (
                      <div
                        className={classNames(
                          'incoming--message',
                          `incoming--message--channel__${channel}`,
                          {
                            'incoming--message--emoji-only': isEmojiOnly,
                          },
                        )}
                      >
                        <Popover
                          isOpen={hoverMessageKey === messageKey}
                          position="right"
                          padding={30}
                          content={() => (
                            <div className="incoming__message--popover">
                              {moment(Number(message.time)).format(
                                'YYYY-MM-DD HH:mm',
                              )}
                            </div>
                          )}
                        >
                          <div>
                            {isSearch ? (
                              <SearchResultText
                                message={message}
                                messageText={displayText}
                                maybeGetRef={maybeGetRef}
                                getMatchHighlightClassName={
                                  getMatchHighlightClassName
                                }
                                searchString={searchString}
                              />
                            ) : (
                              <Linkify>{displayText}</Linkify>
                            )}
                            {otnConsent && <>Notify me</>}
                            {!!hiddenBodyText && (
                              <Button
                                size="sm"
                                color="link"
                                onClick={() => setShowHiddenBody(s => !s)}
                              >
                                {showHiddenBody ? 'Hide' : 'Show'} More
                              </Button>
                            )}
                            {showHiddenBody && <div>{hiddenBodyText}</div>}
                          </div>
                        </Popover>
                      </div>
                    )}
                  {showMessageActions && (
                    <div className="incoming--message-action">
                      {(mobilePhone || email) && (
                        <div
                          className="incoming__message--save-to-contact-button"
                          onClick={saveToContact}
                        >
                          Save to contact
                        </div>
                      )}
                      {message.structure.emoji && (
                        <div className="incoming--message-reaction">
                          {message.structure.emoji}
                        </div>
                      )}
                    </div>
                  )}
                  {fallbackAttachment && (
                    <div
                      className="incoming--message incoming--message-fallback-attachment"
                      onClick={openFallbackUrl}
                    >
                      <Popover
                        isOpen={
                          !(displayText || otnConsent) &&
                          hoverMessageKey === messageKey
                        }
                        position="right"
                        padding={30}
                        content={() => (
                          <div className="incoming__message--popover">
                            {moment(Number(message.time)).format(
                              'YYYY-MM-DD HH:mm',
                            )}
                          </div>
                        )}
                      >
                        <>
                          <div className="incoming--message-fallback-attachment-title">
                            {fallbackAttachmentTitle}
                          </div>
                          <div
                            className="incoming--message-fallback-attachment-link"
                            title={fallbackAttachmentUrl}
                          >
                            {fallbackAttachmentUrl}
                          </div>
                        </>
                      </Popover>
                    </div>
                  )}
                  {attachmentLinksWithTypes &&
                    attachmentLinksWithTypes.map((a, idx) => {
                      if (a.type === 'image' && a.url) {
                        return (
                          <ImageMessage
                            key={`attachment-${idx}`}
                            url={a.url}
                            messageDate={message.time}
                            avatar={profilePic}
                            personName={fullName}
                          />
                        );
                      }
                      if (a.type === 'video' && a.url) {
                        return (
                          <VideoAttachement
                            key={`attachment-${idx}`}
                            url={a.url}
                          />
                        );
                      }
                      if (a.type === 'audio' && a.url) {
                        return (
                          <div
                            className="incoming-message-audio__container"
                            key={`attachment-${idx}`}
                          >
                            <div className="incoming-message-audio__title">
                              Voice Message:
                            </div>
                            <AudioAttachement url={a.url} />
                          </div>
                        );
                      }
                      if (a.type === 'file' && a.url) {
                        return (
                          <div
                            className="incoming--message"
                            key={`attachment-${idx}`}
                          >
                            <a
                              className="incoming-message__attachment-link"
                              href={a.url}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <AttachmentDownloadIcon className="incoming-message__attachment-link-icon" />
                              <span className="incoming-message__attachment-link-name">
                                {a.url.substring(
                                  a.url.lastIndexOf('/') + 1,
                                  a.url.lastIndexOf('?'),
                                )}
                              </span>
                            </a>
                          </div>
                        );
                      }
                      if (a.type === 'share' && a.url) {
                        return (
                          <div
                            className="incoming-message-shared-post__container"
                            key={`attachment-${idx}`}
                          >
                            <span className="incoming-message-shared-post__title">
                              Shared post with you:
                            </span>
                            <div
                              className="incoming-message-shared-post__indicator"
                              onClick={() => window.open(a.url)}
                            >
                              <PlayIcon className="ig-story-indicator-icon" />
                              View Post
                            </div>
                          </div>
                        );
                      }

                      return null;
                    })}
                  {imageSrc && (
                    <ImageMessage
                      url={imageSrc}
                      messageDate={message.time}
                      avatar={profilePic}
                      personName={fullName}
                    />
                  )}
                  {videoSrc && (
                    <div className="incoming-message__body-video-container">
                      <video
                        controls
                        muted
                        autoPlay
                        loop
                        className="incoming-message__body-video"
                      >
                        <source src={videoSrc} />
                      </video>
                    </div>
                  )}
                  {audioSrc && (
                    <div className="incoming-message__body-video-container">
                      <audio
                        controls
                        muted
                        className="incoming-message__body-video"
                      >
                        <source src={audioSrc} />
                      </audio>
                    </div>
                  )}
                  {isIGStoryMessage && (
                    <IGStoryMessage
                      message={message}
                      avatar={profilePic}
                      personName={fullName}
                    />
                  )}
                  {isIGCommentGuardMessage && displayText && (
                    <IGCommentGuardMessage displayText={displayText} />
                  )}
                  {isIGLiveCommentMessage && displayText && (
                    <IGLiveCommentMessage displayText={displayText} />
                  )}
                </>
              )}
            </div>
          </div>
          {initiatedFromQnA && (
            <StartedFrom
              payload={
                message.structure.promoterName ? (
                  <>
                    <b>Answer triggered by Q&A keyword:</b>{' '}
                    {message.structure.promoterName}
                  </>
                ) : (
                  <b>Answer triggered by Q&A keyword</b>
                )
              }
              ignorePrephrase={true}
            />
          )}
        </>
      )}
    </>
  );
};
